import React, {useEffect, useMemo, useState} from "react";
import {Link} from "react-router-dom";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import {IconButton} from "@material-ui/core";
import {MdDeleteForever, MdEdit} from "react-icons/md";
import {IoIosArrowBack, IoIosArrowForward} from "react-icons/io";
import AlertDelete from "../../components/Alert/AlertDelete";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableFooter from "@mui/material/TableFooter";
import {useTranslation} from "react-i18next";
import {AiOutlineSearch} from "react-icons/ai";
import {getCurrencyPairs} from "../../services/CurrencyPair";
import currencyPairTypes from "../../constants/CurrencyPairTypes";
import DivDiagonal from "../../components/DivDiagonal";
import {
    TradingWrapper,
    ContentArea,
    HeaderActions,
    NewTradingButton,
} from "../Trading/styles";
import {
    StrategyPageHeader,
    StrategyCardColumn,
    StrategyProductCard,
    StrategyTableContainer,
    StrategyTableWrapper,
    StrategyActionButtons,
    StrategyPaginationInfo,
    StrategyPaginationButton,
    StrategyLoadingButton,
    StrategyEmptyState,
    StrategyNameCell,
    StrategySearchWrap,
} from "../Strategy/styles";

const PER_PAGE = 25;

export default function CurrencyPair() {
    const [loading, setLoading] = useState(true);
    const [allCustom, setAllCustom] = useState([]);
    const [page, setPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");
    const [deleteCurrencyPair, setDeleteCurrencyPair] = useState(null);
    const { t } = useTranslation();

    useEffect(() => {
        setLoading(true);
        getCurrencyPairs({ full: 1 }).then((res) => {
            if (!res || !res.data) {
                setAllCustom([]);
                setLoading(false);
                return;
            }
            const rows = res.data.data;
            setAllCustom(Array.isArray(rows) ? rows : []);
            setLoading(false);
        }).catch(() => {
            setAllCustom([]);
            setLoading(false);
        });

        return function cleanup() {
            setAllCustom([]);
        };
    }, [deleteCurrencyPair === true]);

    const mergedRows = useMemo(() => {
        const customs = allCustom.map((r) => ({
            kind: "custom",
            id: r.id,
            name: r.name,
        }));
        const defaults = currencyPairTypes.map((r) => ({
            kind: "default",
            key: r.key,
            name: r.value,
        }));
        return [...customs, ...defaults];
    }, [allCustom]);

    const filteredRows = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) {
            return mergedRows;
        }
        return mergedRows.filter((row) =>
            String(row.name || "").toLowerCase().includes(q)
        );
    }, [mergedRows, searchQuery]);

    const totalPages = Math.max(1, Math.ceil(filteredRows.length / PER_PAGE));

    const pageSlice = useMemo(() => {
        const start = (page - 1) * PER_PAGE;
        return filteredRows.slice(start, start + PER_PAGE);
    }, [filteredRows, page]);

    useEffect(() => {
        setPage(1);
    }, [searchQuery]);

    useEffect(() => {
        const tp = Math.max(1, Math.ceil(filteredRows.length / PER_PAGE));
        setPage((p) => (p > tp ? tp : p));
    }, [filteredRows.length]);

    const totalListed = filteredRows.length;

    return (
        <TradingWrapper>
            <DivDiagonal />
            <ContentArea>
                <StrategyPageHeader>
                    <h1>
                        <i className="fas fa-coins" aria-hidden="true"/>
                        {' '}{t('currency_pair_list_title')}{' '}
                        <span className="badge">{totalListed}</span>
                    </h1>
                    <HeaderActions>
                        <NewTradingButton as={Link} to="/currency-pair/new">
                            <i className="fas fa-plus"/> {t('add')}
                        </NewTradingButton>
                    </HeaderActions>
                </StrategyPageHeader>

                <StrategyCardColumn>
                    <StrategyProductCard>
                        <StrategyTableWrapper>
                            {loading ? (
                                <StrategyLoadingButton
                                    className="btn btn-primary text-capitalize font-weight-bold"
                                    loading={loading}
                                    type="button"
                                >
                                    {t('loading')}
                                </StrategyLoadingButton>
                            ) : (
                                <>
                                    {mergedRows.length > 0 ? (
                                        <>
                                            <StrategySearchWrap>
                                                <AiOutlineSearch aria-hidden="true"/>
                                                <input
                                                    type="search"
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                    placeholder={t('currency_pair_search_placeholder')}
                                                    aria-label={t('currency_pair_search_placeholder')}
                                                />
                                            </StrategySearchWrap>
                                            {filteredRows.length === 0 ? (
                                                <StrategyEmptyState style={{ padding: "48px 20px" }}>
                                                    <i className="fas fa-search" aria-hidden="true"/>
                                                    <p>{t('currency_pair_search_no_results')}</p>
                                                </StrategyEmptyState>
                                            ) : (
                                            <StrategyTableContainer>
                                                <Table aria-label={t('currency_pair_list_title')}>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>{t('strategy_table_col_name')}</TableCell>
                                                            <TableCell align="center">{t('strategy_table_col_options')}</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {pageSlice.map((row, idx) => (
                                                            <TableRow
                                                                key={
                                                                    row.kind === "custom"
                                                                        ? `c-${row.id}`
                                                                        : `d-${row.key}-${(page - 1) * PER_PAGE + idx}`
                                                                }
                                                            >
                                                                <TableCell>
                                                                    <StrategyNameCell
                                                                        style={
                                                                            row.kind === "default"
                                                                                ? { opacity: 0.92 }
                                                                                : undefined
                                                                        }
                                                                    >
                                                                        {row.name}
                                                                    </StrategyNameCell>
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    {row.kind === "custom" ? (
                                                                        <StrategyActionButtons>
                                                                            <Link to={`/currency-pair/${row.id}/edit`}>
                                                                                <IconButton aria-label="edit">
                                                                                    <MdEdit/>
                                                                                </IconButton>
                                                                            </Link>
                                                                            <IconButton
                                                                                aria-label="delete"
                                                                                onClick={() => setDeleteCurrencyPair(row.id)}
                                                                            >
                                                                                <MdDeleteForever/>
                                                                            </IconButton>
                                                                        </StrategyActionButtons>
                                                                    ) : (
                                                                        <span style={{ color: "rgba(255,255,255,0.2)", fontSize: 13 }}>
                                                                            —
                                                                        </span>
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                    <TableFooter>
                                                        <TableRow>
                                                            <TableCell colSpan={2}>
                                                                <div style={{
                                                                    display: "flex",
                                                                    justifyContent: "space-between",
                                                                    alignItems: "center",
                                                                    flexWrap: "wrap",
                                                                    gap: 12,
                                                                }}>
                                                                    <StrategyPaginationInfo>
                                                                        {t('table_pagination_text', {
                                                                            page,
                                                                            totalPage: totalPages,
                                                                        })}
                                                                    </StrategyPaginationInfo>
                                                                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                                                        <StrategyPaginationButton
                                                                            type="button"
                                                                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                                                                            disabled={page <= 1}
                                                                        >
                                                                            <IoIosArrowBack/> {t('table_pagination_back')}
                                                                        </StrategyPaginationButton>
                                                                        <StrategyPaginationButton
                                                                            type="button"
                                                                            onClick={() => setPage((p) => p + 1)}
                                                                            disabled={page >= totalPages}
                                                                        >
                                                                            {t('table_pagination_next')} <IoIosArrowForward/>
                                                                        </StrategyPaginationButton>
                                                                    </div>
                                                                </div>
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableFooter>
                                                </Table>
                                            </StrategyTableContainer>
                                            )}
                                            {deleteCurrencyPair && typeof deleteCurrencyPair === "string" && (
                                                <AlertDelete
                                                    setSuccessDelete={setDeleteCurrencyPair}
                                                    urlDelete={`${process.env.REACT_APP_API_ENDPOINT}/api/currency-pair/delete/${deleteCurrencyPair}`}
                                                />
                                            )}
                                        </>
                                    ) : (
                                        <StrategyEmptyState>
                                            <i className="fas fa-coins" aria-hidden="true"/>
                                            <p>{t('currency_pair_empty_title')}</p>
                                            <span>{t('currency_pair_empty_subtitle')}</span>
                                        </StrategyEmptyState>
                                    )}
                                </>
                            )}
                        </StrategyTableWrapper>
                    </StrategyProductCard>
                </StrategyCardColumn>
            </ContentArea>
        </TradingWrapper>
    );
}
